// @client/components/profile-manager/QuickEditPopover.tsx
// Quick-Edit Popover for Gender, Industry and similar single-select fields

import React, { useState, useMemo } from "react";
import { Check } from "lucide-react";
import { Badge } from "@client/components/ui/badge";
import { Input } from "@client/components/ui/input";
import { ScrollArea } from "@client/components/ui/scroll-area";
import { Popover, PopoverContent, PopoverTrigger } from "@client/components/ui/popover";
import { cn } from "@client/lib/utils";

export interface QuickEditOption {
  id: string;
  label: string;
  description?: string;
}

export interface QuickEditBadgeProps {
  value: string | null;
  options: QuickEditOption[];
  onSave: (value: string) => Promise<void> | void;
  label: string;
  emptyText?: string;
  variant?: 'default' | 'secondary' | 'outline' | 'destructive';
  disabled?: boolean;
  className?: string;
}

/**
 * QuickEditBadge - Badge with inline popover editor
 * 
 * Usage:
 * <QuickEditBadge
 *   value={profile.gender}
 *   options={genderValues}
 *   onSave={(value) => handleQuickEdit(profile.id, 'gender', value)}
 *   label="Gender"
 *   emptyText="Set"
 * />
 */
export function QuickEditBadge({
  value,
  options,
  onSave,
  label,
  emptyText = "Set",
  variant = 'outline',
  disabled = false,
  className
}: QuickEditBadgeProps) {
  
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  // Find current option
  const currentOption = useMemo(() => {
    return options.find(opt => opt.id === value);
  }, [options, value]);

  // Filter options based on search
  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return options;
    
    const query = searchQuery.toLowerCase();
    return options.filter(opt => 
      opt.label.toLowerCase().includes(query) ||
      opt.description?.toLowerCase().includes(query)
    );
  }, [options, searchQuery]);

  // Handle option selection
  const handleSelect = async (optionId: string) => {
    if (disabled || isSaving) return;
    
    setIsSaving(true);
    try {
      await onSave(optionId);
      setOpen(false);
      setSearchQuery("");
    } catch (error) {
      console.error('[QuickEditBadge] Save failed:', error);
      // Error handling via parent component's mutation hooks
    } finally {
      setIsSaving(false);
    }
  };

  // Handle popover close
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setSearchQuery("");
    }
    setOpen(newOpen);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Badge
          variant={variant}
          className={cn(
            "cursor-pointer hover:opacity-80 transition-opacity",
            disabled && "opacity-50 cursor-not-allowed",
            !currentOption && "text-muted-foreground italic",
            className
          )}
          onClick={() => !disabled && setOpen(true)}
        >
          {currentOption?.label || emptyText}
        </Badge>
      </PopoverTrigger>
      
      <PopoverContent 
        className="w-64 p-0" 
        align="start"
        side="bottom"
      >
        <div className="flex flex-col">
          {/* Header with Search */}
          <div className="p-3 border-b space-y-2">
            <div className="text-sm font-medium">{label}</div>
            
            {options.length > 5 && (
              <Input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-8 text-sm"
                autoFocus
              />
            )}
          </div>
          
          {/* Options List */}
          <ScrollArea className="max-h-60">
            <div className="p-2 space-y-1">
              {filteredOptions.length === 0 ? (
                <div className="text-center py-6 text-sm text-muted-foreground">
                  No options found
                </div>
              ) : (
                filteredOptions.map(option => {
                  const isSelected = option.id === value;
                  
                  return (
                    <button
                      key={option.id}
                      onClick={() => handleSelect(option.id)}
                      disabled={isSaving}
                      className={cn(
                        "w-full flex items-center justify-between px-3 py-2 rounded text-sm text-left",
                        "hover:bg-muted transition-colors",
                        isSelected && "bg-muted font-medium",
                        isSaving && "opacity-50 cursor-not-allowed"
                      )}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="truncate">{option.label}</div>
                        {option.description && (
                          <div className="text-xs text-muted-foreground truncate">
                            {option.description}
                          </div>
                        )}
                      </div>
                      
                      {isSelected && (
                        <Check className="h-4 w-4 ml-2 flex-shrink-0 text-primary" />
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </ScrollArea>
          
          {/* Loading Indicator */}
          {isSaving && (
            <div className="p-3 border-t">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <div className="w-3 h-3 border-2 border-muted-foreground border-t-transparent rounded-full animate-spin" />
                Saving...
              </div>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * QuickEditPopover - Standalone popover (if trigger control needed externally)
 * 
 * Less common use case - QuickEditBadge is preferred for most scenarios
 */
export interface QuickEditPopoverProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value: string | null;
  options: QuickEditOption[];
  onSave: (value: string) => Promise<void> | void;
  label: string;
  children: React.ReactNode;
}

export function QuickEditPopover({
  open,
  onOpenChange,
  value,
  options,
  onSave,
  label,
  children
}: QuickEditPopoverProps) {
  
  const [searchQuery, setSearchQuery] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return options;
    
    const query = searchQuery.toLowerCase();
    return options.filter(opt => 
      opt.label.toLowerCase().includes(query) ||
      opt.description?.toLowerCase().includes(query)
    );
  }, [options, searchQuery]);

  const handleSelect = async (optionId: string) => {
    if (isSaving) return;
    
    setIsSaving(true);
    try {
      await onSave(optionId);
      onOpenChange(false);
      setSearchQuery("");
    } catch (error) {
      console.error('[QuickEditPopover] Save failed:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setSearchQuery("");
    }
    onOpenChange(newOpen);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        {children}
      </PopoverTrigger>
      
      <PopoverContent className="w-64 p-0" align="start">
        <div className="flex flex-col">
          <div className="p-3 border-b space-y-2">
            <div className="text-sm font-medium">{label}</div>
            
            {options.length > 5 && (
              <Input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-8 text-sm"
                autoFocus
              />
            )}
          </div>
          
          <ScrollArea className="max-h-60">
            <div className="p-2 space-y-1">
              {filteredOptions.length === 0 ? (
                <div className="text-center py-6 text-sm text-muted-foreground">
                  No options found
                </div>
              ) : (
                filteredOptions.map(option => {
                  const isSelected = option.id === value;
                  
                  return (
                    <button
                      key={option.id}
                      onClick={() => handleSelect(option.id)}
                      disabled={isSaving}
                      className={cn(
                        "w-full flex items-center justify-between px-3 py-2 rounded text-sm text-left",
                        "hover:bg-muted transition-colors",
                        isSelected && "bg-muted font-medium",
                        isSaving && "opacity-50 cursor-not-allowed"
                      )}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="truncate">{option.label}</div>
                        {option.description && (
                          <div className="text-xs text-muted-foreground truncate">
                            {option.description}
                          </div>
                        )}
                      </div>
                      
                      {isSelected && (
                        <Check className="h-4 w-4 ml-2 flex-shrink-0 text-primary" />
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </ScrollArea>
          
          {isSaving && (
            <div className="p-3 border-t">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <div className="w-3 h-3 border-2 border-muted-foreground border-t-transparent rounded-full animate-spin" />
                Saving...
              </div>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}